import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { useMultiLanguageWebsite, Widget, Section, Column, ResponsiveValue, SpacingValue } from '@/hooks/useMultiLanguageWebsite';
import { 
  Monitor, 
  Tablet, 
  Smartphone, 
  Plus, 
  Trash2, 
  Copy, 
  Move, 
  Lock, 
  Unlock,
  Eye,
  EyeOff,
  Undo,
  Redo,
  Save,
  Settings,
  Type,
  Image as ImageIcon,
  Square,
  Minus,
  MousePointer,
  Grid,
  Layers,
  Palette,
  Spacing,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Bold,
  Italic,
  Underline
} from 'lucide-react';

interface VisualEditorProps {
  pageId: string;
  onClose: () => void;
}

const VisualEditor = ({ pageId, onClose }: VisualEditorProps) => {
  const { toast } = useToast();
  const {
    website,
    currentLanguage,
    devicePreview,
    setDevicePreview,
    selectedElement,
    setSelectedElement,
    editMode,
    setEditMode,
    updateWidget,
    updateSection,
    getPage,
    getCurrentLanguageConfig,
    getResponsiveValue,
    undo,
    redo,
    canUndo,
    canRedo,
    saveWebsite
  } = useMultiLanguageWebsite();

  const [draggedElement, setDraggedElement] = useState<string | null>(null);
  const [showGrid, setShowGrid] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [zoom, setZoom] = useState(100);
  const [activePanel, setActivePanel] = useState<'content' | 'style' | 'advanced'>('content');
  const [isInlineEditing, setIsInlineEditing] = useState(false);
  const [inlineEditValue, setInlineEditValue] = useState('');

  const canvasRef = useRef<HTMLDivElement>(null);
  const page = getPage(pageId);
  const languageConfig = getCurrentLanguageConfig();

  if (!page) return null;

  // Get selected widget/section/column
  const getSelectedElement = () => {
    if (!selectedElement) return null;
    
    const [type, ...idParts] = selectedElement.split('-');
    const id = idParts.join('-');
    
    if (type === 'section') {
      return page.sections.find(s => s.id === id);
    } else if (type === 'widget') {
      for (const section of page.sections) {
        for (const column of section.columns) {
          const widget = column.widgets.find(w => w.id === id);
          if (widget) return widget;
        }
      }
    }
    return null;
  };

  const selectedElementData = getSelectedElement();

  // Responsive value editor component
  const ResponsiveValueEditor = ({ 
    label, 
    value, 
    onChange, 
    type = 'number',
    min = 0,
    max = 1000,
    step = 1,
    unit = 'px'
  }: {
    label: string;
    value: ResponsiveValue<number>;
    onChange: (value: ResponsiveValue<number>) => void;
    type?: 'number' | 'slider';
    min?: number;
    max?: number;
    step?: number;
    unit?: string;
  }) => (
    <div className="space-y-3">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="grid grid-cols-3 gap-2">
        {(['desktop', 'tablet', 'mobile'] as const).map((device) => (
          <div key={device} className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground capitalize">{device}</Label>
              {device === 'desktop' && <Monitor className="h-3 w-3" />}
              {device === 'tablet' && <Tablet className="h-3 w-3" />}
              {device === 'mobile' && <Smartphone className="h-3 w-3" />}
            </div>
            {type === 'slider' ? (
              <div className="space-y-1">
                <Slider
                  value={[value[device]]}
                  onValueChange={([newValue]) => onChange({ ...value, [device]: newValue })}
                  min={min}
                  max={max}
                  step={step}
                  className="w-full"
                />
                <div className="text-xs text-center text-muted-foreground">
                  {value[device]}{unit}
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-1">
                <Input
                  type="number"
                  value={value[device]}
                  onChange={(e) => onChange({ ...value, [device]: parseInt(e.target.value) || 0 })}
                  min={min}
                  max={max}
                  step={step}
                  className="text-xs"
                />
                <span className="text-xs text-muted-foreground">{unit}</span>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  // Spacing editor component
  const SpacingEditor = ({ 
    label, 
    value, 
    onChange 
  }: {
    label: string;
    value: ResponsiveValue<SpacingValue>;
    onChange: (value: ResponsiveValue<SpacingValue>) => void;
  }) => (
    <div className="space-y-3">
      <Label className="text-sm font-medium">{label}</Label>
      <div className="space-y-3">
        {(['desktop', 'tablet', 'mobile'] as const).map((device) => (
          <div key={device} className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground capitalize">{device}</Label>
              {device === 'desktop' && <Monitor className="h-3 w-3" />}
              {device === 'tablet' && <Tablet className="h-3 w-3" />}
              {device === 'mobile' && <Smartphone className="h-3 w-3" />}
            </div>
            <div className="grid grid-cols-4 gap-1">
              {(['top', 'right', 'bottom', 'left'] as const).map((side) => (
                <div key={side} className="space-y-1">
                  <Label className="text-xs text-muted-foreground capitalize">{side}</Label>
                  <Input
                    type="number"
                    value={value[device][side]}
                    onChange={(e) => onChange({
                      ...value,
                      [device]: {
                        ...value[device],
                        [side]: parseInt(e.target.value) || 0
                      }
                    })}
                    className="text-xs"
                    min={0}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Inline editing
  const startInlineEdit = (element: Widget, field: string) => {
    setIsInlineEditing(true);
    setInlineEditValue(element.content[field] || '');
  };

  const saveInlineEdit = (sectionId: string, columnId: string, widgetId: string, field: string) => {
    updateWidget(pageId, sectionId, columnId, widgetId, {
      content: {
        ...selectedElementData?.content,
        [field]: inlineEditValue
      }
    });
    setIsInlineEditing(false);
    setInlineEditValue('');
  };

  // Render widget in canvas
  const renderWidget = (widget: Widget, sectionId: string, columnId: string) => {
    const isSelected = selectedElement === `widget-${widget.id}`;
    const isLocked = widget.locked;

    const widgetStyle = {
      margin: `${getResponsiveValue(widget.layout.margin).top}px ${getResponsiveValue(widget.layout.margin).right}px ${getResponsiveValue(widget.layout.margin).bottom}px ${getResponsiveValue(widget.layout.margin).left}px`,
      padding: `${getResponsiveValue(widget.layout.padding).top}px ${getResponsiveValue(widget.layout.padding).right}px ${getResponsiveValue(widget.layout.padding).bottom}px ${getResponsiveValue(widget.layout.padding).left}px`,
      width: getResponsiveValue(widget.layout.width),
      height: getResponsiveValue(widget.layout.height),
      backgroundColor: widget.layout.backgroundColor,
      borderRadius: `${getResponsiveValue(widget.layout.borderRadius)}px`,
      border: `${getResponsiveValue(widget.layout.border.width)}px ${widget.layout.border.style} ${widget.layout.border.color}`,
      boxShadow: widget.layout.boxShadow,
      zIndex: widget.layout.zIndex,
      fontSize: widget.typography ? `${getResponsiveValue(widget.typography.fontSize)}px` : undefined,
      fontFamily: widget.typography?.fontFamily,
      fontWeight: widget.typography?.fontWeight,
      lineHeight: widget.typography?.lineHeight,
      letterSpacing: `${widget.typography?.letterSpacing}px`,
      textAlign: widget.typography?.textAlign,
      color: widget.typography?.color,
      textTransform: widget.typography?.textTransform
    };

    const renderWidgetContent = () => {
      switch (widget.type) {
        case 'heading':
          const HeadingTag = widget.content.tag || 'h2';
          return (
            <HeadingTag
              style={widgetStyle}
              onClick={() => !isLocked && startInlineEdit(widget, 'text')}
              className={`cursor-pointer ${isInlineEditing ? 'outline-dashed outline-2 outline-primary' : ''}`}
            >
              {isInlineEditing && selectedElement === `widget-${widget.id}` ? (
                <Input
                  value={inlineEditValue}
                  onChange={(e) => setInlineEditValue(e.target.value)}
                  onBlur={() => saveInlineEdit(sectionId, columnId, widget.id, 'text')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      saveInlineEdit(sectionId, columnId, widget.id, 'text');
                    }
                  }}
                  autoFocus
                  className="bg-transparent border-none p-0 text-inherit font-inherit"
                />
              ) : (
                widget.content.text
              )}
            </HeadingTag>
          );

        case 'text':
          return (
            <p
              style={widgetStyle}
              onClick={() => !isLocked && startInlineEdit(widget, 'text')}
              className={`cursor-pointer ${isInlineEditing ? 'outline-dashed outline-2 outline-primary' : ''}`}
            >
              {isInlineEditing && selectedElement === `widget-${widget.id}` ? (
                <Input
                  value={inlineEditValue}
                  onChange={(e) => setInlineEditValue(e.target.value)}
                  onBlur={() => saveInlineEdit(sectionId, columnId, widget.id, 'text')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      saveInlineEdit(sectionId, columnId, widget.id, 'text');
                    }
                  }}
                  autoFocus
                  className="bg-transparent border-none p-0 text-inherit font-inherit"
                />
              ) : (
                widget.content.text
              )}
            </p>
          );

        case 'button':
          return (
            <button
              style={widgetStyle}
              onClick={() => !isLocked && startInlineEdit(widget, 'text')}
              className={`cursor-pointer transition-all hover:opacity-80 ${isInlineEditing ? 'outline-dashed outline-2 outline-primary' : ''}`}
            >
              {isInlineEditing && selectedElement === `widget-${widget.id}` ? (
                <Input
                  value={inlineEditValue}
                  onChange={(e) => setInlineEditValue(e.target.value)}
                  onBlur={() => saveInlineEdit(sectionId, columnId, widget.id, 'text')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      saveInlineEdit(sectionId, columnId, widget.id, 'text');
                    }
                  }}
                  autoFocus
                  className="bg-transparent border-none p-0 text-inherit font-inherit text-center"
                />
              ) : (
                widget.content.text
              )}
            </button>
          );

        case 'image':
          return (
            <img
              src={widget.image?.src || '/placeholder.svg'}
              alt={widget.image?.alt || ''}
              style={{
                ...widgetStyle,
                objectFit: widget.image?.objectFit,
                objectPosition: widget.image?.objectPosition
              }}
              className="cursor-pointer"
            />
          );

        case 'spacer':
          return (
            <div
              style={widgetStyle}
              className="bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-500 text-sm"
            >
              Spacer ({getResponsiveValue(widget.layout.height)})
            </div>
          );

        default:
          return (
            <div
              style={widgetStyle}
              className="bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-500 text-sm"
            >
              {widget.type} Widget
            </div>
          );
      }
    };

    return (
      <div
        key={widget.id}
        className={`relative group ${isSelected ? 'ring-2 ring-primary ring-offset-2' : ''} ${isLocked ? 'opacity-75' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedElement(`widget-${widget.id}`);
        }}
        draggable={!isLocked}
        onDragStart={() => setDraggedElement(`widget-${widget.id}`)}
        onDragEnd={() => setDraggedElement(null)}
      >
        {renderWidgetContent()}
        
        {/* Widget Controls */}
        {isSelected && editMode && (
          <div className="absolute -top-8 left-0 flex items-center space-x-1 bg-primary text-primary-foreground px-2 py-1 rounded text-xs">
            <span>{widget.name}</span>
            {isLocked ? (
              <Lock className="h-3 w-3" />
            ) : (
              <Unlock className="h-3 w-3" />
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-4 w-4 p-0 text-primary-foreground hover:text-primary-foreground"
              onClick={(e) => {
                e.stopPropagation();
                // Copy widget
              }}
            >
              <Copy className="h-3 w-3" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-4 w-4 p-0 text-primary-foreground hover:text-primary-foreground"
              onClick={(e) => {
                e.stopPropagation();
                // Delete widget
              }}
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
    );
  };

  // Render section in canvas
  const renderSection = (section: Section) => {
    const isSelected = selectedElement === `section-${section.id}`;
    const isLocked = section.locked;

    const sectionStyle = {
      margin: `${getResponsiveValue(section.layout.margin).top}px ${getResponsiveValue(section.layout.margin).right}px ${getResponsiveValue(section.layout.margin).bottom}px ${getResponsiveValue(section.layout.margin).left}px`,
      padding: `${getResponsiveValue(section.layout.padding).top}px ${getResponsiveValue(section.layout.padding).right}px ${getResponsiveValue(section.layout.padding).bottom}px ${getResponsiveValue(section.layout.padding).left}px`,
      backgroundColor: section.layout.backgroundColor,
      backgroundImage: section.layout.backgroundImage ? `url(${section.layout.backgroundImage})` : undefined,
      minHeight: getResponsiveValue(section.layout.minHeight),
      borderRadius: `${getResponsiveValue(section.layout.borderRadius)}px`,
      border: `${getResponsiveValue(section.layout.border.width)}px ${section.layout.border.style} ${section.layout.border.color}`,
      boxShadow: section.layout.boxShadow,
      display: getResponsiveValue(section.layout.display),
      flexDirection: getResponsiveValue(section.layout.flexDirection),
      justifyContent: getResponsiveValue(section.layout.justifyContent),
      alignItems: getResponsiveValue(section.layout.alignItems),
      gap: `${getResponsiveValue(section.layout.gap)}px`
    };

    return (
      <section
        key={section.id}
        className={`relative ${isSelected ? 'ring-2 ring-primary ring-offset-4' : ''} ${isLocked ? 'opacity-75' : ''} ${!section.enabled ? 'opacity-50' : ''}`}
        style={sectionStyle}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedElement(`section-${section.id}`);
        }}
        draggable={!isLocked}
        onDragStart={() => setDraggedElement(`section-${section.id}`)}
        onDragEnd={() => setDraggedElement(null)}
      >
        {/* Section Controls */}
        {isSelected && editMode && (
          <div className="absolute -top-10 left-0 flex items-center space-x-1 bg-primary text-primary-foreground px-3 py-1 rounded text-sm z-50">
            <span>{section.name}</span>
            <Badge variant="secondary" className="text-xs">
              {section.type}
            </Badge>
            {isLocked ? (
              <Lock className="h-4 w-4" />
            ) : (
              <Unlock className="h-4 w-4" />
            )}
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 text-primary-foreground hover:text-primary-foreground"
              onClick={(e) => {
                e.stopPropagation();
                // Toggle section visibility
                updateSection(pageId, section.id, { enabled: !section.enabled });
              }}
            >
              {section.enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            </Button>
          </div>
        )}

        {/* Columns */}
        <div className="flex w-full h-full" style={{ gap: `${getResponsiveValue(section.layout.gap)}px` }}>
          {section.columns.map((column) => (
            <div
              key={column.id}
              className="flex-1 min-h-[50px] border-2 border-dashed border-gray-200 hover:border-gray-400 transition-colors"
              style={{
                width: `${getResponsiveValue(column.width)}%`,
                padding: `${getResponsiveValue(column.layout.padding).top}px ${getResponsiveValue(column.layout.padding).right}px ${getResponsiveValue(column.layout.padding).bottom}px ${getResponsiveValue(column.layout.padding).left}px`
              }}
              onDrop={(e) => {
                e.preventDefault();
                // Handle widget drop
              }}
              onDragOver={(e) => {
                e.preventDefault();
              }}
            >
              {column.widgets.map((widget) => renderWidget(widget, section.id, column.id))}
              
              {/* Add Widget Button */}
              {editMode && (
                <div className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 hover:border-primary transition-colors cursor-pointer">
                  <Plus className="h-6 w-6 text-gray-400" />
                  <span className="ml-2 text-gray-400">Add Widget</span>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    );
  };

  return (
    <div className="fixed inset-0 bg-background z-50 flex">
      {/* Left Sidebar - Elements & Layers */}
      <div className="w-80 bg-card border-r border-border overflow-y-auto">
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Visual Editor</h2>
            <Button variant="ghost" size="sm" onClick={onClose}>
              ×
            </Button>
          </div>
          
          <div className="flex items-center space-x-2 mb-4">
            <Button
              variant={editMode ? 'default' : 'outline'}
              size="sm"
              onClick={() => setEditMode(!editMode)}
            >
              <MousePointer className="h-4 w-4 mr-1" />
              {editMode ? 'Exit Edit' : 'Edit Mode'}
            </Button>
            <Button
              variant={showGrid ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowGrid(!showGrid)}
            >
              <Grid className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={undo}
              disabled={!canUndo}
            >
              <Undo className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={redo}
              disabled={!canRedo}
            >
              <Redo className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => saveWebsite(website)}
            >
              <Save className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <Tabs defaultValue="elements" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="elements">Elements</TabsTrigger>
            <TabsTrigger value="layers">Layers</TabsTrigger>
          </TabsList>
          
          <TabsContent value="elements" className="p-4 space-y-4">
            <div className="space-y-2">
              <h3 className="font-medium">Basic Elements</h3>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" size="sm" className="justify-start">
                  <Type className="h-4 w-4 mr-2" />
                  Text
                </Button>
                <Button variant="outline" size="sm" className="justify-start">
                  <Type className="h-4 w-4 mr-2" />
                  Heading
                </Button>
                <Button variant="outline" size="sm" className="justify-start">
                  <ImageIcon className="h-4 w-4 mr-2" />
                  Image
                </Button>
                <Button variant="outline" size="sm" className="justify-start">
                  <Square className="h-4 w-4 mr-2" />
                  Button
                </Button>
                <Button variant="outline" size="sm" className="justify-start">
                  <Minus className="h-4 w-4 mr-2" />
                  Spacer
                </Button>
                <Button variant="outline" size="sm" className="justify-start">
                  <Minus className="h-4 w-4 mr-2" />
                  Divider
                </Button>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="layers" className="p-4">
            <div className="space-y-2">
              <h3 className="font-medium">Page Structure</h3>
              <div className="space-y-1">
                {page.sections.map((section) => (
                  <div
                    key={section.id}
                    className={`p-2 rounded cursor-pointer flex items-center justify-between ${
                      selectedElement === `section-${section.id}` ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                    }`}
                    onClick={() => setSelectedElement(`section-${section.id}`)}
                  >
                    <div className="flex items-center space-x-2">
                      <Layers className="h-4 w-4" />
                      <span className="text-sm">{section.name}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      {section.locked && <Lock className="h-3 w-3" />}
                      {!section.enabled && <EyeOff className="h-3 w-3" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Main Canvas */}
      <div className="flex-1 flex flex-col">
        {/* Top Toolbar */}
        <div className="h-16 bg-card border-b border-border flex items-center justify-between px-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Label className="text-sm">Device:</Label>
              <div className="flex items-center space-x-1 bg-muted rounded-lg p-1">
                <Button
                  variant={devicePreview === 'desktop' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setDevicePreview('desktop')}
                >
                  <Monitor className="h-4 w-4" />
                </Button>
                <Button
                  variant={devicePreview === 'tablet' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setDevicePreview('tablet')}
                >
                  <Tablet className="h-4 w-4" />
                </Button>
                <Button
                  variant={devicePreview === 'mobile' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setDevicePreview('mobile')}
                >
                  <Smartphone className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Label className="text-sm">Zoom:</Label>
              <Slider
                value={[zoom]}
                onValueChange={([value]) => setZoom(value)}
                min={25}
                max={200}
                step={25}
                className="w-24"
              />
              <span className="text-sm text-muted-foreground">{zoom}%</span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Badge variant="outline">
              {languageConfig.nativeName} ({languageConfig.direction.toUpperCase()})
            </Badge>
            <Button variant="outline" size="sm">
              Preview
            </Button>
          </div>
        </div>

        {/* Canvas Area */}
        <div className="flex-1 overflow-auto bg-gray-50 p-8">
          <div
            ref={canvasRef}
            className={`mx-auto bg-white shadow-lg transition-all duration-300 ${
              showGrid ? 'bg-grid-pattern' : ''
            }`}
            style={{
              width: devicePreview === 'desktop' ? '1200px' : devicePreview === 'tablet' ? '768px' : '375px',
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              direction: languageConfig.direction
            }}
          >
            {page.sections
              .sort((a, b) => a.order - b.order)
              .map((section) => renderSection(section))}
          </div>
        </div>
      </div>

      {/* Right Sidebar - Properties */}
      <div className="w-80 bg-card border-l border-border overflow-y-auto">
        <div className="p-4">
          <h3 className="font-semibold mb-4">Properties</h3>
          
          {selectedElementData ? (
            <Tabs value={activePanel} onValueChange={(value: any) => setActivePanel(value)}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="content">Content</TabsTrigger>
                <TabsTrigger value="style">Style</TabsTrigger>
                <TabsTrigger value="advanced">Advanced</TabsTrigger>
              </TabsList>
              
              <TabsContent value="content" className="space-y-4 mt-4">
                {selectedElementData.type === 'heading' && (
                  <div className="space-y-3">
                    <div>
                      <Label>Text</Label>
                      <Input
                        value={selectedElementData.content.text || ''}
                        onChange={(e) => {
                          // Update widget content
                        }}
                      />
                    </div>
                    <div>
                      <Label>Tag</Label>
                      <Select value={selectedElementData.content.tag || 'h2'}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="h1">H1</SelectItem>
                          <SelectItem value="h2">H2</SelectItem>
                          <SelectItem value="h3">H3</SelectItem>
                          <SelectItem value="h4">H4</SelectItem>
                          <SelectItem value="h5">H5</SelectItem>
                          <SelectItem value="h6">H6</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </TabsContent>
              
              <TabsContent value="style" className="space-y-4 mt-4">
                {selectedElementData.typography && (
                  <div className="space-y-4">
                    <h4 className="font-medium">Typography</h4>
                    
                    <div>
                      <Label>Font Family</Label>
                      <Select value={selectedElementData.typography.fontFamily}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Cairo">Cairo</SelectItem>
                          <SelectItem value="Noto Sans Arabic">Noto Sans Arabic</SelectItem>
                          <SelectItem value="Montserrat">Montserrat</SelectItem>
                          <SelectItem value="Roboto">Roboto</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <ResponsiveValueEditor
                      label="Font Size"
                      value={selectedElementData.typography.fontSize}
                      onChange={(value) => {
                        // Update typography
                      }}
                      type="slider"
                      min={8}
                      max={72}
                      unit="px"
                    />

                    <div className="flex items-center space-x-2">
                      <Label>Text Align</Label>
                      <div className="flex items-center space-x-1">
                        <Button variant="outline" size="sm">
                          <AlignLeft className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                          <AlignCenter className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                          <AlignRight className="h-4 w-4" />
                        </Button>
                        <Button variant="outline" size="sm">
                          <AlignJustify className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div>
                      <Label>Color</Label>
                      <div className="flex items-center space-x-2">
                        <Input
                          type="color"
                          value={selectedElementData.typography.color}
                          className="w-12 h-8"
                        />
                        <Input
                          value={selectedElementData.typography.color}
                          className="flex-1"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <Separator />

                <div className="space-y-4">
                  <h4 className="font-medium">Layout</h4>
                  
                  <SpacingEditor
                    label="Margin"
                    value={selectedElementData.layout.margin}
                    onChange={(value) => {
                      // Update layout margin
                    }}
                  />

                  <SpacingEditor
                    label="Padding"
                    value={selectedElementData.layout.padding}
                    onChange={(value) => {
                      // Update layout padding
                    }}
                  />

                  <div>
                    <Label>Background Color</Label>
                    <div className="flex items-center space-x-2">
                      <Input
                        type="color"
                        value={selectedElementData.layout.backgroundColor}
                        className="w-12 h-8"
                      />
                      <Input
                        value={selectedElementData.layout.backgroundColor}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <ResponsiveValueEditor
                    label="Border Radius"
                    value={selectedElementData.layout.borderRadius}
                    onChange={(value) => {
                      // Update border radius
                    }}
                    type="slider"
                    min={0}
                    max={50}
                    unit="px"
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="advanced" className="space-y-4 mt-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Locked</Label>
                    <Switch
                      checked={selectedElementData.locked}
                      onCheckedChange={(checked) => {
                        // Update locked state
                      }}
                    />
                  </div>

                  <div>
                    <Label>CSS Classes</Label>
                    <Input placeholder="custom-class another-class" />
                  </div>

                  <div>
                    <Label>Custom CSS</Label>
                    <textarea
                      className="w-full h-24 p-2 border rounded text-sm font-mono"
                      placeholder="/* Custom CSS */"
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Select an element to edit its properties</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VisualEditor;